import { downloadTicketsCsv, getErrorMessageFromPossibleBlob } from '@gozoki/api'
import type { CartStatus, CartsWithReportUrl } from '@gozoki/api-types'
import { Button, Chip, IconButton, Paper, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import type { GridColDef, GridSortModel } from '@mui/x-data-grid'
import { DateTimePicker } from '@mui/x-date-pickers'
import { DEFAULT_STORE_ID } from '@gozoki/tools'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Eye, User } from 'lucide-react'
import { DateTime } from 'luxon'
import { useCallback, useState } from 'react'
import { z } from 'zod/v3'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import Page from '../../components/Page'
import { AuthorizedButton } from '../../components/communication/AuthorizedButton'
import { useToast } from '../../components/communication/Toast'
import SimpleDataGrid from '../../components/containers/SimpleDataGrid'
import { CustomerInfoDialog } from '../../components/routes/carts/CustomerInfoDialog'
import { StoresPicker } from '../../utils/dashboard/storesPicker'
import { fonts } from '../../utils/theme/fonts.theme'

export const Route = createFileRoute('/_authenticated/carts')({
  staticData: {
    permission: ['read', 'carts.carts.cart'],
  },
  component: CartsPage,
  validateSearch: z.object({
    storeId: z.number().catch(DEFAULT_STORE_ID).default(DEFAULT_STORE_ID),
  }),
})
withRoutePermissions(Route)

const CartStatus = ({ cart }: { cart: CartsWithReportUrl }) => {
  switch (cart.status) {
    case 'pending': {
      return <Chip style={{ height: 35 }} label="En attente de validation" color="primary" />
    }
    case 'validated': {
      return <Chip style={{ height: 35 }} label="Validé" color="success" />
    }
    case 'cancelled': {
      return <Chip style={{ height: 35 }} label="Annulé" color="warning" />
    }
    case 'stolen': {
      return <Chip style={{ height: 35 }} label="Volé" color="error" />
    }
    default: {
      cart.status satisfies never
      return null
    }
  }
}

const cartsColumnDef = ({
  onCustomerInfoClick,
}: {
  onCustomerInfoClick: (cartId: number) => void
}) => {
  const gridColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'N° de panier',
      width: 200,
    },
    {
      field: 'createdAt',
      headerName: 'Date et heure de création',
      sortable: true,
      valueFormatter: (params) =>
        DateTime.fromISO(params.value as string)
          .setZone('Europe/Paris')
          .setLocale('fr')
          .toFormat("'le' d MMMM 'à' HH'h'mm'm'ss's'"),
      width: 260,
    },
    {
      field: 'articles',
      headerName: "Nb d'articles",
      valueFormatter: (params) => params.value.length,
      width: 180,
    },
    {
      field: 'amountTotal',
      flex: 1,
      headerName: 'Prix total',
      valueFormatter: (params) => `${params.value} €`,
      width: 180,
    },
    {
      field: 'userId',
      headerName: 'Utilisateur application ?',
      sortable: false,
      width: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.row.userId != null ? (
          <Tooltip title="Voir la fiche client">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onCustomerInfoClick(params.row.id as number)
              }}
              aria-label="Voir la fiche client"
            >
              <User size={18} />
            </IconButton>
          </Tooltip>
        ) : null,
    },
    {
      field: 'status',
      headerName: 'Statut',
      renderCell: (params) => <CartStatus cart={params.row as CartsWithReportUrl} />,
      width: 200,
    },
    {
      field: 'reportUrl',
      headerName: '',
      renderCell: (params) => (
        <a
          target="_blank"
          href={`${params.row.reportUrl}`}
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <Button variant="outlined" color="success" style={{ width: '100%' }}>
            <Eye />
            &nbsp;Voir la facture
          </Button>
        </a>
      ),
      width: 200,
    },
  ]
  return gridColumns
}

type CartsTab = 'all' | 'pending'

function CartsPage() {
  const navigate = Route.useNavigate()
  const { storeId: currentStore } = Route.useSearch()

  const setCurrentStore = useCallback(
    (storeId: number) => {
      void navigate({ search: (prev) => ({ ...prev, storeId }) })
    },
    [navigate],
  )

  const [tab, setTab] = useState<CartsTab>('all')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState(20)
  const [startDate, setStartDate] = useState<DateTime | null>(() =>
    DateTime.now().minus({ hours: 24 }),
  )
  const [endDate, setEndDate] = useState<DateTime | null>(() => DateTime.now())
  const [selectedCartId, setSelectedCartId] = useState<number | null>(null)
  const [Toast, showToast] = useToast()
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }])

  const sortBy = sortModel[0]?.field === 'createdAt' ? 'created_at' : 'updated_at'
  const sortDirection = sortModel[0]?.sort ?? 'desc'
  const cartStatus: CartStatus | null = tab === 'pending' ? 'pending' : null

  const { data: carts, isLoading } = useQuery(
    api.stores.getCartsData.queryOptions(
      {
        params: { storeId: currentStore },
        query: {
          page: page + 1,
          pageSize,
          startDate: startDate?.toISO() ?? undefined,
          endDate: endDate?.toISO() ?? undefined,
          cartStatus: cartStatus ?? undefined,
          sortBy,
          sortDirection,
        },
      },
      { placeholderData: keepPreviousData },
    ),
  )

  const { mutate: mutateDownloadAuditFile, isPending: isLoadingAudit } = useMutation({
    mutationFn: downloadTicketsCsv,
    onError: async (error) => {
      const errorMessage = await getErrorMessageFromPossibleBlob(error)

      showToast({ message: errorMessage, severity: 'error' })
    },
    onSuccess: (response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `liens-factures_${startDate?.toFormat('yyyy-MM-dd')}_${endDate?.toFormat(
          'yyyy-MM-dd',
        )}.xlsx`,
      )
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
    },
  })

  const handleDownloadAllTickets = useCallback(() => {
    const usedStartDate = (startDate ?? DateTime.now().minus({ days: 31 })).toISO()
    const usedEndDate = (endDate ?? DateTime.now()).toISO()
    if (!usedStartDate || !usedEndDate) {
      return
    }
    mutateDownloadAuditFile({
      dateEnd: usedEndDate,
      dateStart: usedStartDate,
      storeId: currentStore,
    })
  }, [mutateDownloadAuditFile, startDate, endDate, currentStore])

  return (
    <Page>
      <Toast />
      <div style={{ display: 'flex', paddingLeft: 10 }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Paniers
        </Typography>
      </div>
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', flex: 3, padding: '16px' }}>
          <div style={{ flex: 1 }}>
            <StoresPicker
              storeSwitch
              onlyBoutiques
              defaultStore={currentStore}
              setUniqueStore={setCurrentStore}
            />
          </div>
        </div>
      </Paper>

      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', gap: '12px', padding: '16px' }}>
          <Typography>Du</Typography>
          <DateTimePicker
            label="Date de départ"
            value={startDate}
            onChange={(date: DateTime | null) => setStartDate(date || null)}
            maxDate={endDate ?? DateTime.now()}
            ampm={false}
            format="dd/MM/yyyy HH:mm"
          />
          <Typography>au</Typography>
          <DateTimePicker
            label="Date de fin"
            value={endDate}
            onChange={(date: DateTime | null) => setEndDate(date || null)}
            disabled={startDate === null}
            minDate={startDate ? startDate.plus({ minute: 1 }) : undefined}
            maxDate={DateTime.now()}
            ampm={false}
            format="dd/MM/yyyy HH:mm"
          />
          <Button
            variant="contained"
            color="secondary"
            style={{ marginLeft: 16 }}
            onClick={() => {
              setStartDate(null)
              setEndDate(null)
            }}
            disabled={startDate === null && endDate === null}
          >
            Réinitialiser le filtre
          </Button>
          <div style={{ flexGrow: 1 }}>
            <AuthorizedButton
              permission={['export', 'carts.carts.cart']}
              variant="contained"
              color="info"
              onClick={handleDownloadAllTickets}
              disabled={isLoadingAudit}
            >
              Exports des tickets
            </AuthorizedButton>
          </div>
        </div>
        <Tabs
          value={tab}
          onChange={(_, value: CartsTab) => {
            setTab(value)
            setPage(0)
          }}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab value="all" label="Tous" />
          <Tab value="pending" label="En attente" />
        </Tabs>
        <SimpleDataGrid
          columns={cartsColumnDef({ onCustomerInfoClick: setSelectedCartId })}
          rows={
            carts?.data?.map((c) => ({
              ...c,
              articles: c.articles ?? [],
            })) ?? []
          }
          loading={isLoading}
          rowCount={carts?.meta.total ?? 0}
          pageSizeOptions={[20, 50]}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            // Keep only the first sort (single-column sorting)
            setSortModel(model.slice(0, 1))
          }}
          initialState={{
            pagination: {
              paginationModel: { page, pageSize },
            },
          }}
          onPaginationModelChange={(params) => {
            setPage(params.page)
            setPageSize(params.pageSize)
          }}
          rowSelection={false}
          disableRowSelectionOnClick
          checkboxSelection={false}
        />
      </Paper>
      <CustomerInfoDialog
        cartId={selectedCartId}
        open={selectedCartId != null}
        onClose={() => setSelectedCartId(null)}
      />
    </Page>
  )
}
